/**
 * Training **module** domain types, shared between server and web.
 *
 * A training module is an admin-authored e-learning course: an ordered list
 * of pages, each either a content slide or a quiz question. Like onboarding
 * forms it is **versioned**: publishing inserts a new immutable version row,
 * so an attempt always resolves to the exact content (and pass mark) it was
 * taken against.
 *
 * Storage rules (same as the rest of onboarding, see ../onboarding.ts):
 *   - The *definition* lives in training_modules / training_module_versions.
 *   - An attempt's *answers* live in training_attempts (redactable for GDPR),
 *     never on the event chain.
 *   - A PII-free proof event (TrainingAttemptCompleted) records that an
 *     attempt happened, with the score, on the tamper-evident chain.
 *
 * The leak boundary: correct answers exist only in the authoring schema.
 * Inductees receive a TrainingPlayerPage projection that structurally has no
 * answer fields (no correct_* keys, no hotspot regions, server-shuffled
 * ordering/matching). Grading is exclusively server-side.
 *
 * Shapes only. The publish-time rules live in ./validate, the derived
 * readers (question list, total points) in ./queries.
 */

/* -------------------------------------------------------------------------
 * Page / question types
 * -----------------------------------------------------------------------*/

export const TRAINING_QUESTION_TYPES = [
  'single_choice',
  'multi_select',
  'true_false',
  'short_text',
  'categorize',
  'ordering',
  'hotspot',
  'matching',
] as const;
export type TrainingQuestionType = (typeof TRAINING_QUESTION_TYPES)[number];

/**
 * Page types that only make sense in an assessment that is not marked:
 * they collect text rather than test knowledge, so there is no answer key
 * to author and nothing for the grader to score.
 */
export const TRAINING_UNMARKED_ONLY_PAGE_TYPES = ['text_group', 'follow_up'] as const;
export type TrainingUnmarkedOnlyPageType = (typeof TRAINING_UNMARKED_ONLY_PAGE_TYPES)[number];

export const TRAINING_PAGE_TYPES = [
  'slide',
  'question_group',
  ...TRAINING_UNMARKED_ONLY_PAGE_TYPES,
  ...TRAINING_QUESTION_TYPES,
] as const;
export type TrainingPageType = (typeof TRAINING_PAGE_TYPES)[number];

/**
 * Pages that are not a single question slot: slides, and the multi-row /
 * derived pages. Question banks (random subset, shuffle) fill one slot per
 * page and so cannot express these; see trainingPlayer#applyQuestionServe.
 */
export const TRAINING_FIXED_PAGE_TYPES = [
  'slide',
  'question_group',
  ...TRAINING_UNMARKED_ONLY_PAGE_TYPES,
] as const;

/**
 * How a failed module is retried. Set per module (on the version, so the
 * rules in force at publish time govern historical attempts):
 *   full_retake — answers are graded once at the end; a fail shows score +
 *                 pass/fail only (never which questions were wrong) and the
 *                 inductee restarts the whole module from the first slide.
 *                 Unlimited retakes; every attempt is recorded.
 *   try_again   — each question is graded immediately on submit and the
 *                 inductee retries it until correct before moving on. Every
 *                 try is recorded; the score for reporting is the first-try
 *                 percentage.
 */
export const TRAINING_RETRY_MODES = ['full_retake', 'try_again'] as const;
export type TrainingRetryMode = (typeof TRAINING_RETRY_MODES)[number];

/**
 * The two shapes a module can take.
 *   quiz     — the graded e-learning course (slides + questions, a pass mark);
 *              the default, and everything else in this file describes it.
 *   external — a redirect-and-self-certify module. The learner opens an
 *              external URL, completes the training there, then attests they
 *              have done so. No on-platform content, no grading, no pass mark.
 *              Its `schema` is empty ({ pages: [] }); the url / instructions /
 *              confirmation live on dedicated version columns, not the schema.
 * The learner's attestation (a TaskSubmitted event authored by them, on the
 * tamper-evident chain) is the completion record — the "honesty" is auditable.
 */
export const TRAINING_MODULE_KINDS = ['quiz', 'external'] as const;
export type TrainingModuleKind = (typeof TRAINING_MODULE_KINDS)[number];

/**
 * Who an "available to everyone" module reaches when the daily sweep fans it
 * out. 'all' = every training person (including existing staff). 'new_starters'
 * = genuine onboarding starters only, skipping existing staff (anyone flagged
 * training_learner or onboarding_hidden). Only meaningful when assign_to_all.
 */
export const TRAINING_ASSIGN_ALL_SCOPES = ['all', 'new_starters'] as const;
export type TrainingAssignAllScope = (typeof TRAINING_ASSIGN_ALL_SCOPES)[number];

/** Sensible default attestation, offered by the builder for external modules. */
export const DEFAULT_EXTERNAL_CONFIRMATION =
  'I confirm I have completed this training on the linked site.';

/** A selectable choice for single_choice / multi_select questions. */
export interface TrainingOption {
  id: string;
  label: string;
}

/**
 * A correct area on a hotspot image. Coordinates are normalised 0..1
 * relative to the rendered image so the same region works at any display
 * size. Rectangles only in v1.
 */
export interface TrainingHotspotRegion {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** One left/right pairing in a matching question. */
export interface TrainingMatchPair {
  id: string;
  left: string;
  right: string;
}

/**
 * One item to sort in a categorize question, with the category it belongs in.
 * `category_id` is the answer and is stripped from the player projection.
 */
export interface TrainingCategoryItem {
  id: string;
  label: string;
  category_id: string;
}

/** Keys shared by every question page. */
export interface TrainingQuestionBase {
  /** Stable id, unique within the module. Keys the submitted answer. */
  id: string;
  prompt: string;
  /** Weight in the module total. Positive integer; defaults to 1 in the builder. */
  points: number;
  /** Optional illustration shown above the answers. Storage key. */
  image_key?: string;
  /** The section this page belongs to (see TrainingModuleSchema.sections). */
  section_id?: string;
  /**
   * When true the player lets the learner move on and finish without
   * answering. Defaults to required. Meant for free-text follow-ups
   * ("if you answered No anywhere, tell us why"), which are only filled in
   * some of the time; an unanswered question still scores zero when graded.
   */
  optional?: boolean;
}

/** Authoring shape (admin-only). Carries the correct answers. */
export type TrainingQuestionDef =
  | (TrainingQuestionBase & {
      type: 'single_choice';
      options: TrainingOption[];
      /** Omitted only in an ungraded module (see TrainingModuleVersion.graded). */
      correct_option_id?: string;
    })
  | (TrainingQuestionBase & {
      type: 'multi_select';
      options: TrainingOption[];
      correct_option_ids?: string[];
    })
  | (TrainingQuestionBase & {
      type: 'true_false';
      /** Whether the statement in `prompt` is true. */
      correct?: boolean;
    })
  | (TrainingQuestionBase & {
      type: 'short_text';
      /**
       * Accepted answers. The typed answer matches if, after trimming,
       * lower-casing and collapsing inner whitespace, it equals any of these.
       * Server-only (never projected to the player). Omitted when ungraded.
       */
      accepted?: string[];
      /**
       * Render a multi-line textarea rather than a single-line input — the
       * "supporting comments" box at the end of an assessment. Presentation
       * only; the answer is still a plain string.
       */
      multiline?: boolean;
      /** Grey hint shown inside an empty box. */
      placeholder?: string;
      /**
       * Which control collects the answer. Defaults to 'text'. Set by text
       * group rows (a questionnaire's date of birth, start time); the answer
       * is still a plain string in the browser's own format (2026-09-10,
       * 14:30, 2026-09-10T14:30).
       */
      input?: TrainingTextInputKind;
    })
  | (TrainingQuestionBase & {
      type: 'categorize';
      /** The buckets items are sorted into. */
      categories: TrainingOption[];
      /** Items to sort; each carries the category it belongs in. */
      items: TrainingCategoryItem[];
    })
  | (TrainingQuestionBase & {
      type: 'ordering';
      /** Authored array order IS the correct order. Displayed shuffled. */
      items: TrainingOption[];
    })
  | (TrainingQuestionBase & {
      type: 'hotspot';
      image_key: string;
      regions: TrainingHotspotRegion[];
    })
  | (TrainingQuestionBase & {
      type: 'matching';
      pairs: TrainingMatchPair[];
    });

/* -------------------------------------------------------------------------
 * Question groups
 *
 * One page holding several questions that share a single set of answer
 * columns — the classic assessment grid ("Is your keyboard separate from the
 * screen? No / Yes"). The group is **layout only**: each row is a real
 * single_choice question with its own id and points, so grading, scoring,
 * sections, the answers map and the stored attempt detail all keep the shape
 * they have for standalone questions. See ./queries#trainingQuestions, which
 * flattens groups into their rows for every reader.
 * -----------------------------------------------------------------------*/

/** Where a group's illustration sits relative to the rows. */
export const GROUP_IMAGE_POSITIONS = ['above', 'below', 'left', 'right'] as const;
export type GroupImagePosition = (typeof GROUP_IMAGE_POSITIONS)[number];

/**
 * One row of a question group: a single_choice question whose options are
 * the group's shared columns, so only the prompt and the correct column
 * vary per row.
 */
export interface TrainingGroupRowDef {
  id: string;
  prompt: string;
  points: number;
  /** Id of the group option that is correct. Omitted when ungraded. */
  correct_option_id?: string;
  /** See TrainingQuestionBase.optional. */
  optional?: boolean;
}

export interface TrainingQuestionGroupDef {
  id: string;
  type: 'question_group';
  /** The section this page belongs to (see TrainingModuleSchema.sections). */
  section_id?: string;
  /** Group heading, e.g. "Keyboards". */
  title?: string;
  /** Optional lead-in paragraph above the grid. */
  intro?: string;
  /** The answer columns every row shares, left to right. */
  options: TrainingOption[];
  rows: TrainingGroupRowDef[];
  /** Optional illustration. Storage key, same store as slide images. */
  image_key?: string;
  /** Defaults to 'below' when an image is set. */
  image_position?: GroupImagePosition;
  /** Horizontal placement for the above/below positions. Defaults to 'center'. */
  image_align?: SlideImageAlign;
  /** Defaults to 'medium'. */
  image_size?: SlideImageSize;
  image_caption?: string;
  image_alt?: string;
}

/* -------------------------------------------------------------------------
 * Text groups ("Questions and answers")
 *
 * One page holding several free-text questions, each with its own box: the
 * questionnaire shape ("Describe any medication you take"). Like a question
 * group it is layout only: every row is a real short_text question with its
 * own id, so answers, the stored attempt and the answers PDF keep the shape
 * they have for standalone questions. Only valid in an assessment that is
 * not marked; a typed answer with an answer key is the standalone
 * short_text ("Fill in the blank") page.
 * -----------------------------------------------------------------------*/

/**
 * The control a text answer is collected with. 'text' is a typed box (single
 * or multi-line); the rest are the browser's native pickers, whose values
 * arrive as ISO-style strings.
 */
export const TRAINING_TEXT_INPUT_KINDS = ['text', 'date', 'time', 'datetime'] as const;
export type TrainingTextInputKind = (typeof TRAINING_TEXT_INPUT_KINDS)[number];

/** Where a text group's answer boxes sit relative to their prompts. */
export const TEXT_GROUP_LAYOUTS = ['stacked', 'inline'] as const;
export type TextGroupLayout = (typeof TEXT_GROUP_LAYOUTS)[number];

/** One row of a text group: a short_text question without an answer key. */
export interface TrainingTextGroupRowDef {
  id: string;
  prompt: string;
  /** Kept for the flattened question shape; always 0 in practice. */
  points: number;
  /** Defaults to 'text'. */
  input?: TrainingTextInputKind;
  /** Render a multi-line box rather than a single-line input (text only). */
  multiline?: boolean;
  placeholder?: string;
  /** See TrainingQuestionBase.optional. */
  optional?: boolean;
}

export interface TrainingTextGroupDef {
  id: string;
  type: 'text_group';
  /** The section this page belongs to (see TrainingModuleSchema.sections). */
  section_id?: string;
  /** Page heading, e.g. "About your health". */
  title?: string;
  /** Optional lead-in paragraph above the questions. */
  intro?: string;
  /**
   * 'stacked' (default) puts each box under its prompt; 'inline' puts the
   * prompt and box side by side, the compact form for short answers.
   */
  layout?: TextGroupLayout;
  rows: TrainingTextGroupRowDef[];
}

/* -------------------------------------------------------------------------
 * Follow-up pages
 *
 * A page whose questions are not authored but derived at run time: it looks
 * back over the answers given so far, picks every choice question (or grid
 * row) whose chosen option matches `match_label` (for example every "Yes"),
 * and asks the learner to explain each one in a text box. The explanations
 * are stored as ONE answer under the page id, a map of source question id
 * to text, the same shape a matching answer uses, so no question ids need to
 * be invented per attempt. Contributes no questions to the count or the
 * grader; only valid in an assessment that is not marked.
 * -----------------------------------------------------------------------*/

export interface TrainingFollowUpDef {
  id: string;
  type: 'follow_up';
  /** The section this page belongs to (see TrainingModuleSchema.sections). */
  section_id?: string;
  title?: string;
  intro?: string;
  /**
   * The option label to look for, compared trimmed and case-insensitively
   * ("Yes" matches "yes "). True/false questions match "True" / "False".
   */
  match_label: string;
  /**
   * Restrict the lookup to these earlier pages. Empty or omitted means every
   * choice page before this one in the module.
   */
  source_page_ids?: string[];
  placeholder?: string;
  /** Defaults to true: explanations are usually a sentence or more. */
  multiline?: boolean;
  /**
   * When true the learner may move on with explanations missing. Defaults
   * to required: every matched question needs some text.
   */
  optional?: boolean;
}

/** Where a slide's image sits relative to the body text. */
export const SLIDE_IMAGE_LAYOUTS = ['below', 'left', 'right', 'full'] as const;
export type SlideImageLayout = (typeof SLIDE_IMAGE_LAYOUTS)[number];

export const SLIDE_IMAGE_SIZES = ['small', 'medium', 'large'] as const;
export type SlideImageSize = (typeof SLIDE_IMAGE_SIZES)[number];

/** Horizontal placement of an image block; defaults to left when unset. */
export const SLIDE_IMAGE_ALIGNS = ['left', 'center', 'right'] as const;
export type SlideImageAlign = (typeof SLIDE_IMAGE_ALIGNS)[number];

export const SLIDE_CALLOUT_TONES = ['info', 'tip', 'success', 'warning', 'danger'] as const;
export type SlideCalloutTone = (typeof SLIDE_CALLOUT_TONES)[number];

/**
 * Structured slide content: an ordered list of typed blocks, the successor
 * to the single body string. `text` blocks still accept the body markup
 * subset (so bold / inline lists keep working inside them); the other
 * types give the builder dedicated editors and consistent styling.
 * `columns` is the one container type and may only hold leaf blocks —
 * a single nesting level keeps both the renderer and the editor sane.
 */
export type SlideLeafBlock =
  | { id: string; type: 'heading'; text: string }
  | { id: string; type: 'text'; text: string }
  | { id: string; type: 'list'; style: 'bullets' | 'numbered'; items: string[] }
  | { id: string; type: 'callout'; tone: SlideCalloutTone; text: string }
  | {
      id: string;
      type: 'image';
      image_key: string;
      size?: SlideImageSize | 'full';
      /** Horizontal placement within the slide; defaults to left when unset. */
      align?: SlideImageAlign;
      caption?: string;
      alt?: string;
    }
  | { id: string; type: 'quote'; text: string; attribution?: string }
  /** A big centred standalone line — title slides, key messages. */
  | { id: string; type: 'statement'; text: string }
  /** An embedded PDF (policy / handout) read inline. `file_key` is a storage key. */
  | { id: string; type: 'pdf'; file_key: string; caption?: string }
  /**
   * A self-hosted video played inline (HTML5 <video>). `file_key` is the
   * serving URL of an mp4/webm uploaded direct-to-S3; the player watch-gates
   * it (blocks "Next" until watched). Shares the gated file store with images
   * and the pdf block, so it reuses `file_key` (the access gate already covers
   * that field) rather than a new key name.
   */
  | { id: string; type: 'video'; file_key: string; caption?: string }
  | { id: string; type: 'table'; header: string[]; rows: string[][] }
  | { id: string; type: 'divider' };

export type SlideContentBlock =
  | SlideLeafBlock
  | { id: string; type: 'columns'; left: SlideLeafBlock[]; right: SlideLeafBlock[] };

export type SlideBlockType = SlideContentBlock['type'];

/**
 * A content slide: title + body text + optional image. No value, no grading.
 *
 * The body is plain text with a small markup subset, rendered as text nodes
 * (never raw HTML): blank line = new paragraph, `## ` = subheading, `- ` =
 * bullet item, `1. ` = numbered item, `> ` = callout box, `**bold**`, and
 * bare URLs become links. Shared between the player and the builder preview
 * via the web's slideMarkup parser.
 */
export interface TrainingSlideDef {
  id: string;
  type: 'slide';
  /** The section this page belongs to (see TrainingModuleSchema.sections). */
  section_id?: string;
  title?: string;
  /**
   * Structured content. When present (even empty) the slide is
   * block-based and the legacy body/image_* fields below are ignored.
   */
  blocks?: SlideContentBlock[];
  body?: string;
  image_key?: string;
  /** Defaults to 'below' when an image is set. */
  image_layout?: SlideImageLayout;
  /** Defaults to 'medium'. Ignored for the 'full' layout. */
  image_size?: SlideImageSize;
  /** Small caption rendered under the image. */
  image_caption?: string;
  /** Accessible description of the image for screen readers. */
  image_alt?: string;
}

export type TrainingPageDef =
  | TrainingSlideDef
  | TrainingQuestionGroupDef
  | TrainingTextGroupDef
  | TrainingFollowUpDef
  | TrainingQuestionDef;

/**
 * A named group of pages with an optional pass mark of its own. Sections are
 * metadata over the flat page list (a page joins one via its `section_id`),
 * not nesting, so the builder and the slot model stay simple. When a section
 * sets `pass_mark` the learner must reach it within that section in addition
 * to the module's overall pass mark; sections without one are visual grouping.
 */
export interface TrainingSection {
  id: string;
  title: string;
  /** Percentage 0..100 to reach within the section. Omitted = ungated. */
  pass_mark?: number;
}

/** The full schema stored in training_module_versions.schema (JSONB). */
export interface TrainingModuleSchema {
  pages: TrainingPageDef[];
  /** Optional pass-gated / grouping sections over the pages. */
  sections?: TrainingSection[];
}

/* -------------------------------------------------------------------------
 * Player projection (what an inductee is allowed to see)
 *
 * Structurally answer-free: no correct_* keys, no hotspot regions, and the
 * ordering items / matching right column arrive pre-shuffled by the server
 * so authored position never leaks. Built by the server's playerView
 * service; the web player and the builder preview both render these.
 * -----------------------------------------------------------------------*/

export type TrainingPlayerQuestion =
  | (TrainingQuestionBase & { type: 'single_choice'; options: TrainingOption[] })
  | (TrainingQuestionBase & { type: 'multi_select'; options: TrainingOption[] })
  | (TrainingQuestionBase & { type: 'true_false' })
  | (TrainingQuestionBase & {
      type: 'short_text';
      multiline?: boolean;
      placeholder?: string;
      input?: TrainingTextInputKind;
    })
  | (TrainingQuestionBase & {
      type: 'categorize';
      categories: TrainingOption[];
      /** Items to place; category_id stripped, order shuffled. */
      items: TrainingOption[];
    })
  | (TrainingQuestionBase & { type: 'ordering'; items: TrainingOption[] })
  | (TrainingQuestionBase & { type: 'hotspot'; image_key: string })
  | (TrainingQuestionBase & {
      type: 'matching';
      /** Left column, authored order. id = pair id. */
      targets: TrainingOption[];
      /** Right column, shuffled, with derived ids that do not equal pair ids. */
      labels: TrainingOption[];
    });

/** A question group with every row's correct column stripped. */
export interface TrainingPlayerQuestionGroup {
  id: string;
  type: 'question_group';
  section_id?: string;
  title?: string;
  intro?: string;
  options: TrainingOption[];
  rows: { id: string; prompt: string; points: number; optional?: boolean }[];
  image_key?: string;
  image_position?: GroupImagePosition;
  image_align?: SlideImageAlign;
  image_size?: SlideImageSize;
  image_caption?: string;
  image_alt?: string;
}

/**
 * Text groups and follow-up pages carry no answer key, so their player shape
 * is their authoring shape. Named aliases keep the projection honest about
 * which side of the leak boundary a value sits on.
 */
export type TrainingPlayerTextGroup = TrainingTextGroupDef;
export type TrainingPlayerFollowUp = TrainingFollowUpDef;

export type TrainingPlayerPage =
  | TrainingSlideDef
  | TrainingPlayerQuestionGroup
  | TrainingPlayerTextGroup
  | TrainingPlayerFollowUp
  | TrainingPlayerQuestion;

/* -------------------------------------------------------------------------
 * Answers
 * -----------------------------------------------------------------------*/

/**
 * The submitted answer for one question, keyed by question id (a group's
 * rows answer individually, under their own row ids):
 *   single_choice — the chosen option id
 *   multi_select  — the chosen option ids
 *   true_false    — the chosen boolean
 *   ordering      — item ids in the chosen order
 *   hotspot       — the tapped point, normalised 0..1
 *   matching      — target (pair) id -> placed label id
 *   follow_up     — keyed by the PAGE id: source question id -> explanation
 *   text_group    — rows answer individually under their own ids (a string)
 */
export type TrainingAnswer =
  | string
  | string[]
  | boolean
  | { x: number; y: number }
  | Record<string, string>;

export type TrainingAnswers = Record<string, TrainingAnswer>;

/* -------------------------------------------------------------------------
 * Module + version entities (mirror onboarding forms)
 * -----------------------------------------------------------------------*/

export interface TrainingModule {
  id: string;
  /** Stable slug, e.g. 'slips-trips-falls-office'. */
  key: string;
  title: string;
  created_at: string; // ISO 8601
  archived_at: string | null;
}

export interface TrainingModuleVersion {
  id: string;
  module_id: string;
  /** Monotonic per module, starting at 1. */
  version: number;
  title: string;
  description: string | null;
  /**
   * quiz (graded course, the default) or external (redirect + self-certify).
   * The three external_* fields below are only meaningful when kind is
   * 'external'; a quiz leaves them null and carries its content in `schema`.
   */
  kind: TrainingModuleKind;
  /** external only: the URL the learner opens to complete the training. */
  external_url: string | null;
  /** external only: instructions shown above the "Open training" button. */
  external_instructions: string | null;
  /** external only: the statement the learner ticks to self-certify. */
  external_confirmation: string | null;
  /**
   * False for an assessment that collects answers rather than testing
   * knowledge (DSE self-assessment and the like): no correct answers are
   * authored, nothing is marked, every submitted attempt completes the task,
   * and no certificate is issued. On the version so an attempt is always read
   * back under the rules it was taken against.
   */
  graded: boolean;
  /** Percentage 0..100 an attempt must reach to pass. Always 0 when ungraded. */
  pass_mark: number;
  retry_mode: TrainingRetryMode;
  /**
   * Question bank size: serve a random subset of this many questions per
   * attempt. Null serves every question. On the version so an attempt is
   * always scored against the bank rules in force when it was published.
   */
  serve_question_count: number | null;
  /** Draw the served questions in a per-attempt shuffled order. */
  shuffle_questions: boolean;
  schema: TrainingModuleSchema;
  published_at: string | null;
  created_at: string; // ISO 8601
}

/**
 * Builder working state autosaved on training_modules.draft. Publishing a
 * version clears it (same lifecycle as onboarding form drafts).
 */
export interface TrainingModuleDraft {
  title: string;
  description: string;
  /**
   * quiz (default) or external. Optional so drafts saved before external
   * modules existed read back as quiz; readers treat an absent kind as 'quiz'.
   */
  kind?: TrainingModuleKind;
  /** external only. See TrainingModuleVersion for the three external_* fields. */
  external_url?: string | null;
  external_instructions?: string | null;
  external_confirmation?: string | null;
  /** Optional so pre-existing drafts read back as graded. */
  graded?: boolean;
  pass_mark: number;
  retry_mode: TrainingRetryMode;
  /** Question bank size; null serves all. See TrainingModuleVersion. */
  serve_question_count: number | null;
  shuffle_questions: boolean;
  schema: TrainingModuleSchema;
}

/* -------------------------------------------------------------------------
 * Attempt / progress shapes
 * -----------------------------------------------------------------------*/

/** Per-person training state surfaced on /me task rows and the HR matrix. */
export type TrainingStatus = 'not_started' | 'in_progress' | 'passed';

/**
 * Summary of a person's standing on one assigned module. `in_progress`
 * covers both a saved draft and failed attempts awaiting a retake.
 */
export interface TrainingTaskSummary {
  status: TrainingStatus;
  /** False = an unmarked assessment: there is no score to show. */
  graded?: boolean;
  attempts: number;
  /** Best score across completed attempts, 0..100. Null before any attempt. */
  best_score: number | null;
  /** Set once a passing attempt exists. */
  passed_at: string | null;
  /** True once the completion certificate has been issued. */
  certificate_available: boolean;
}

/** One row of the HR matrix attempt-history view (and the /me result list). */
export interface TrainingAttemptSummary {
  id: string;
  attempt_number: number;
  score: number;
  passed: boolean;
  completed_at: string; // ISO 8601
}

export interface TrainingSchemaValidationResult {
  valid: boolean;
  errors: string[];
}
